import type {
  AlertAssignmentSelection,
  AppliedFilter,
  DateBucketItem,
  FilterArrayItem,
  ShipmentFiltersPayload,
} from '../types';
import {
  normalizeFilterLogic,
  supportsFilterLogic,
} from '../../Filters/filterLogic';

const BOOLEAN_ACTIVITY_FILTER_KEYS = new Set([
  'has_comments',
  'unread_mentions',
]);

export const SHIPMENT_DATE_KEYS = new Set<string>([
  'scheduled_delivery_date',
  'scheduled_pickup_date',
  'customer_defined_ship_date',
  'created_at',
  'last_updated',
]);

export const buildShipmentFiltersPayload = (
  filterArrayArg: FilterArrayItem[],
  appliedFiltersArg: AppliedFilter[]
): ShipmentFiltersPayload | null => {
  if (!filterArrayArg || !filterArrayArg.length) return null;

  const out: ShipmentFiltersPayload = {};
  for (const item of filterArrayArg) {
    const key = item.filterBy;
    if (!key) continue;

    if (key === 'missing_pod') {
      const ids = Array.isArray(item.filterValue)
        ? item.filterValue.map((value) => String(value))
        : [String(item.filterValue)];

      if (ids.length) out.missing_pod = ids;
      continue;
    }

    const values = Array.isArray(item.filterValue)
      ? item.filterValue.map((value) => String(value))
      : [String(item.filterValue)];

    if (SHIPMENT_DATE_KEYS.has(key)) continue;

    if (key === 'alert_assignment') {
      // TODO: Confirm recipient user IDs and the assignment filter contract with backend.
      const selections: AlertAssignmentSelection[] = values.flatMap(
        (value): AlertAssignmentSelection[] => {
          if (value === 'unassigned') return [{ type: 'unassigned' }];
          const [type, id] = value.split(':');
          if (!id) return [];
          if (type === 'role') return [{ type, key: id }];
          if (type === 'team' || type === 'user') return [{ type, id }];
          return [];
        }
      );
      if (selections.length) out[key] = selections;
      continue;
    }

    if (BOOLEAN_ACTIVITY_FILTER_KEYS.has(key)) {
      out[key] = true;
      continue;
    }

    if (key === 'type') {
      const nums = values
        .map((value) => Number(value))
        .filter((value) => !Number.isNaN(value));

      if (!nums.length) continue;

      const current = out.type ?? [];
      const merged = Array.from(new Set<number>([...current, ...nums]));
      out.type = merged;
      continue;
    }

    const existing = out[key];
    let existingValues: string[] = [];

    if (Array.isArray(existing)) {
      existingValues = existing.map((value) => String(value));
    } else if (
      existing &&
      typeof existing === 'object' &&
      'values' in existing &&
      Array.isArray((existing as { values?: unknown }).values)
    ) {
      existingValues = (existing as { values: unknown[] }).values.map((value) =>
        String(value)
      );
    }

    const next = new Set<string>(existingValues);
    values.forEach((value) => next.add(value));
    const nextValues = Array.from(next);
    out[key] = supportsFilterLogic(key)
      ? {
          values: nextValues,
          logic: normalizeFilterLogic(item.logic),
        }
      : nextValues;
  }

  for (const item of filterArrayArg) {
    const key = item.filterBy;
    if (!SHIPMENT_DATE_KEYS.has(key)) continue;

    const ids = Array.isArray(item.filterValue)
      ? item.filterValue.map((value) => String(value))
      : [String(item.filterValue)];

    const matches = (appliedFiltersArg || []).filter(
      (appliedFilter) =>
        (appliedFilter.dateFilter === key || appliedFilter.filterBy === key) &&
        ids.includes(String(appliedFilter.id))
    );

    if (!matches.length) continue;

    const bucket: Record<number, DateBucketItem> = {};

    type ParsedId = {
      period?: string;
      start?: string;
      end?: string;
    };

    matches.forEach((appliedFilter, index) => {
      let parsedId: ParsedId | null = null;

      try {
        parsedId = JSON.parse(String(appliedFilter.id)) as ParsedId;
      } catch {
        parsedId = null;
      }

      const period: string | undefined =
        typeof appliedFilter.period === 'string'
          ? appliedFilter.period
          : parsedId && typeof parsedId.period === 'string'
            ? parsedId.period
            : undefined;

      const start: string | undefined =
        typeof appliedFilter.start === 'string'
          ? appliedFilter.start
          : parsedId?.start;

      const end: string | undefined =
        typeof appliedFilter.end === 'string'
          ? appliedFilter.end
          : parsedId?.end;

      if (start || end) {
        bucket[index] = {
          period,
          start,
          end,
        };
      }
    });

    if (Object.keys(bucket).length > 0) {
      out[key] = bucket;
    }
  }

  return Object.keys(out).length ? out : null;
};
